import React, { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../auth/AuthContext';

const STATE_BG = {
  draft:     { bg: '#374151', fg: '#cbd5e1' },
  submitted: { bg: '#6b4c00', fg: '#fff1be' },
  approved:  { bg: '#0a4d22', fg: '#b7eecc' },
  rejected:  { bg: '#6b1b1b', fg: '#f6c2c2' },
  changes_requested: { bg: '#5c2a6b', fg: '#e9c9f4' },
};

const REVIEWER_ROLES = new Set(['superadmin', 'admin', 'reviewer']);
const ANALYST_ROLES = new Set(['superadmin', 'admin', 'reviewer', 'operator']);

/**
 * Phase 4 — Review controls embedded on the run-detail page.
 *
 * - Analyst / Operator: can submit a successful run for review.
 * - Reviewer / Admin: can approve / reject / request-changes (Module 8).
 */
export const ReviewControls = ({ run, onUpdated }) => {
  const { token, user } = useAuth();
  const role = user?.role;
  const review = run?.review || { state: 'draft' };
  const state = review.state || 'draft';
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');

  const canSubmit = useMemo(
    () => ANALYST_ROLES.has(role) && run?.status === 'succeeded'
      && (state === 'draft' || state === 'rejected' || state === 'changes_requested'),
    [role, run?.status, state],
  );
  const canDecide = useMemo(
    () => REVIEWER_ROLES.has(role) && state === 'submitted',
    [role, state],
  );

  const post = useCallback(async (path, body) => {
    setBusy(path);
    setError('');
    try {
      const r = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/runs/${run.id}${path}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body || { notes: notes.trim() }),
        },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status} — ${t.slice(0, 200)}`);
      }
      setNotes('');
      onUpdated?.(await r.json());
    } catch (e) {
      setError(e.message || 'Review action failed.');
    } finally {
      setBusy('');
    }
  }, [token, run?.id, notes, onUpdated]);

  const tone = STATE_BG[state] || STATE_BG.draft;

  return (
    <div
      data-testid="review-controls"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        padding: '0.85rem 1rem',
        borderRadius: 6,
        background: 'var(--ux-bg-soft, #1a2530)',
        border: '1px solid var(--ux-border, #2c3a48)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
        <span
          data-testid="review-state-pill"
          style={{
            background: tone.bg, color: tone.fg,
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.7rem', padding: '2px 10px', borderRadius: 14,
            fontWeight: 700, letterSpacing: '0.06em',
          }}
        >
          REVIEW · {(state || 'draft').toUpperCase().replace('_', ' ')}
        </span>
        {review.submitted_by && (
          <span style={{ fontSize: '0.72rem', color: 'var(--ux-text-muted)' }}>
            submitted by <code>{review.submitted_by}</code>
            {review.submitted_at ? ` at ${review.submitted_at}` : ''}
          </span>
        )}
        {review.reviewer_id && (
          <span style={{ fontSize: '0.72rem', color: 'var(--ux-text-muted)' }}>
            decided by <code>{review.reviewer_id}</code>
            {review.reviewed_at ? ` at ${review.reviewed_at}` : ''}
          </span>
        )}
      </div>

      {(canSubmit || canDecide) && (
        <textarea
          data-testid="review-notes-input"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={canDecide ? 'Reviewer notes (required on reject / request-changes)…' : 'Submission notes (optional)…'}
          rows={2}
          style={{
            width: '100%', padding: '0.45rem 0.6rem',
            fontFamily: '"JetBrains Mono", monospace', fontSize: '0.78rem',
            background: 'var(--ux-bg, #0f1419)',
            color: 'var(--ux-text, #eef3f8)',
            border: '1px solid var(--ux-border, #2c3a48)',
            borderRadius: 4, resize: 'vertical',
          }}
        />
      )}

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
        {canSubmit && (
          <button
            type="button"
            data-testid="review-submit-btn"
            onClick={() => post('/submit-for-review')}
            disabled={!!busy}
            style={btn('#0f1419', '#eef3f8')}
          >
            {busy === '/submit-for-review' ? 'Submitting…' : 'Submit for review'}
          </button>
        )}
        {canDecide && (
          <>
            <button
              type="button"
              data-testid="review-approve-btn"
              onClick={() => post('/approve')}
              disabled={!!busy}
              style={btn('#0a4d22', '#b7eecc')}
            >
              {busy === '/approve' ? 'Approving…' : 'Approve'}
            </button>
            <button
              type="button"
              data-testid="review-reject-btn"
              onClick={() => {
                if (!notes.trim()) {
                  setError('Notes are required when rejecting.');
                  return;
                }
                post('/reject');
              }}
              disabled={!!busy}
              style={btn('#6b1b1b', '#f6c2c2')}
            >
              {busy === '/reject' ? 'Rejecting…' : 'Reject'}
            </button>
            {/* Module 8 — request changes (soft reject; analyst iterates). */}
            <button
              type="button"
              data-testid="review-request-changes-btn"
              onClick={() => {
                if (!notes.trim()) {
                  setError('Notes are required when requesting changes.');
                  return;
                }
                post('/request-changes');
              }}
              disabled={!!busy}
              style={btn('#5c2a6b', '#e9c9f4')}
            >
              {busy === '/request-changes' ? 'Requesting…' : 'Request changes'}
            </button>
          </>
        )}
      </div>

      {(review.submitted_notes || review.review_notes) && (
        <div style={{ fontSize: '0.72rem', color: 'var(--ux-text-muted)' }}>
          {review.submitted_notes && <div>submission notes: <em>{review.submitted_notes}</em></div>}
          {review.review_notes && <div>reviewer notes: <em>{review.review_notes}</em></div>}
        </div>
      )}
      {error && (
        <span data-testid="review-error" style={{ color: '#b15a00', fontSize: '0.72rem' }}>{error}</span>
      )}
    </div>
  );
};

const btn = (bg, fg) => ({
  fontFamily: '"JetBrains Mono", monospace',
  fontSize: '0.72rem', padding: '6px 12px', borderRadius: 6,
  background: bg, color: fg, border: 'none', cursor: 'pointer',
  letterSpacing: '0.04em',
});

export default ReviewControls;
